import React, { useState } from "react"
import { Link, graphql } from "gatsby"

import SEO from "../components/common/seo"
import Layout from "../components/common/layout"
import TopicCard from "../components/topic/topiccard"
import TopicFilters from "../components/topic/filters"
import FeatureIcons from "../components/feature/featureIcons"
import CommunitiesTable from "../components/community/communitiestable"
import TopicStatsCard from "../components/topic/topicStatsCard"
import TopicPageFAQ from "../components/topic/topicPageFaq"

import { unslugify } from "../utils/text"
import { sortCommunities, useCommunityListSort } from "../utils/communities"
import { SIZES } from "../constants/memberSize"
import { topicListUpdatedLabel } from "../utils/topics"
import { buildFeaturePageFAQ } from "../utils/featurePageFaq"
import { getSchemaForFeature } from "../utils/schema"

function platformSlugs(c) {
  return (c.platform || "")
    .split(",")
    .map(s => s.trim())
    .filter(Boolean)
}

const FeaturePage = ({ data }) => {
  const [topicsToShow, setTopicsToShow] = useState(9)
  const [subtitleExpanded, setSubtitleExpanded] = useState(false)

  const feature = data.feature
  const communities = data.communities.nodes
    .filter(c => c[feature.slug])
    .sort((a, b) =>
      a.members < b.members ? 1 : b.members < a.members ? -1 : 0
    )
  const count = communities.length

  const [sizeFilters, setSizeFilters] = useState([])
  const [platformFilters, setPlatformFilters] = useState([])
  const { sort, setSort, sortOptions } = useCommunityListSort()

  const platformFilterOptions = data.allPlatforms.nodes
    .filter(p => !p.removed)
    .map(p => ({
      ...p,
      count: communities.filter(c => platformSlugs(c).includes(p.slug)).length,
    }))
    .filter(p => p.count > 0)
    .sort((a, b) => (a.count < b.count ? 1 : b.count < a.count ? -1 : 0))

  var description = `Discover${count ? " " + count + " " : " "} communities ${
    feature.sentenceEnding
  }`
  if (communities.length > 0) {
    description += ", like "
    description += communities
      .slice(0, 3)
      .map(c => c.name)
      .join(", ")
    description += " and more."
  } else {
    description += "."
  }

  var title = ""
  if (feature.prefix) {
    title += `${unslugify(feature.prefix)} communit${count === 1 ? "y" : "ies"}`
  } else {
    title += `Communit${count === 1 ? "y" : "ies"} ${feature.sentenceEnding}`
  }

  const updatedLabel = topicListUpdatedLabel(null, communities)

  var topicCounts = {}
  communities
    .map(c => c.topics)
    .forEach(slugs => {
      const topics = slugs ? slugs.split(",") : []
      topics.forEach(t => {
        if (topicCounts[t]) {
          topicCounts[t]["count"] += 1
        } else if (data.topics.nodes.filter(topic => topic.slug === t)[0]) {
          topicCounts[t] = {
            topic: data.topics.nodes.filter(topic => topic.slug === t)[0],
            count: 1,
          }
        }
      })
    })

  var sizes = SIZES.map(name => ({
    name,
    count: communities.filter(c => c.memberSize === name).length,
  }))

  var filteredCommunities = communities
  if (sizeFilters.length) {
    filteredCommunities = filteredCommunities.filter(c =>
      sizeFilters.map(f => f.name).includes(c.memberSize)
    )
  }
  if (platformFilters.length) {
    filteredCommunities = filteredCommunities.filter(c =>
      platformFilters.some(pf => platformSlugs(c).includes(pf.slug))
    )
  }

  filteredCommunities = sortCommunities(sort, filteredCommunities)

  const templateFeatureSlugs = data.allFeatures.nodes.map(f => f.slug)
  const faq = buildFeaturePageFAQ({
    feature,
    communities,
    platforms: data.allPlatforms.nodes,
    features: data.allFeatures.nodes,
    topicNodes: data.topics.nodes,
    updatedDate: updatedLabel,
    siteName: data.site.siteMetadata.title,
    templateFeatureSlugs,
  })

  return (
    <Layout pageTitle={title} enableShare={true} noBackground={true}>
      <SEO
        title={count ? `${count} ${title}` : title}
        description={description}
        imageUrl={feature.iconActive}
        generateImage={true}
        path={`/features/${feature.slug}/`}
        schema={getSchemaForFeature({
          feature,
          description,
          filteredCommunities: communities,
          faq,
        })}
      />

      <div
        className={`grid grid-cols-1 gap-4 items-start lg:grid-cols-4 lg:gap-8`}
      >
        <div className="grid grid-cols-1 gap-4 lg:col-span-3">
          <div className="rounded-lg bg-white overflow-hidden shadow p-6">
            <div className="relative">
              <div
                className="absolute inset-0 flex items-center"
                aria-hidden="true"
              >
                <div className="w-full border-t border-gray-200" />
              </div>
              <div className="relative flex items-center justify-between">
                <h2 className="min-w-0 pr-3 bg-white text-xl font-medium text-gray-900">
                  {filteredCommunities.length} online{" "}
                  {filteredCommunities.length === 1
                    ? "community"
                    : "communities"}{" "}
                  {feature.sentenceEnding}
                </h2>
                <span className="hidden sm:inline shrink-0 pl-3 bg-white text-sm text-gray-500">
                  Updated {updatedLabel}
                </span>
              </div>
            </div>

            {feature.description && String(feature.description).trim() ? (
              <button
                type="button"
                aria-expanded={subtitleExpanded}
                onClick={() => setSubtitleExpanded(v => !v)}
                className={`mt-2 block w-full border-0 bg-transparent p-0 text-left text-sm font-normal text-gray-500 leading-snug hover:text-gray-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-rose-500 focus-visible:ring-offset-2 rounded-sm ${
                  subtitleExpanded
                    ? "whitespace-normal"
                    : "cursor-pointer truncate"
                }`}
              >
                {feature.description}
              </button>
            ) : null}

            <div className="mt-3 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4 sm:gap-y-2">
              <div className="min-w-0 sm:flex-1">
                <FeatureIcons
                  wrapperClassName="mt-0"
                  featureNodes={data.allFeatures.nodes}
                  currentFeatureSlug={feature.slug}
                  communities={data.communities.nodes}
                />
              </div>
              {count >= 10 ? (
                <div className="w-full min-w-0 sm:w-auto sm:ml-auto sm:shrink-0">
                  <TopicFilters
                    platformFilterOptions={platformFilterOptions}
                    platformFilters={platformFilters}
                    setPlatformFilters={setPlatformFilters}
                    sizes={sizes}
                    sizeFilters={sizeFilters}
                    setSizeFilters={setSizeFilters}
                    sort={sort}
                    setSort={setSort}
                    sortOptions={sortOptions}
                  />
                </div>
              ) : null}
            </div>

            {count > 0 ? (
              <div className="mt-1">
                <CommunitiesTable
                  communities={filteredCommunities}
                  showTopics={true}
                  linkModal={true}
                  showRankings={true}
                />
              </div>
            ) : (
              <div className="mt-1">
                <div
                  style={{
                    padding: "20px 0 0",
                    textAlign: "center",
                    fontWeight: "500",
                    color: "#95a5a6",
                  }}
                >
                  There are no online communities with this feature yet <br />
                  <br />
                  Do you know one? Please <Link to="/submit/">submit it</Link>!
                </div>
              </div>
            )}

            {Object.keys(topicCounts).length > 1 ? (
              <div className="mt-8">
                <div className="relative">
                  <div
                    className="absolute inset-0 flex items-center"
                    aria-hidden="true"
                  >
                    <div className="w-full border-t border-gray-200" />
                  </div>
                  <div className="relative flex items-center justify-between">
                    <h2 className="pr-3 bg-white text-xl font-medium text-gray-900">
                      Topics with this community feature
                    </h2>
                  </div>
                </div>

                <div className="mt-1 text-gray-500">
                  <ul
                    className={`mt-2 grid grid-cols-2 gap-x-4 gap-y-8 sm:grid-cols-3 sm:gap-x-6 lg:grid-cols-3 xl:gap-x-8`}
                  >
                    {Object.keys(topicCounts)
                      .map(key => topicCounts[key])
                      .sort((a, b) =>
                        a.count < b.count ? 1 : b.count < a.count ? -1 : 0
                      )
                      .map((t, i) => (
                        <TopicCard
                          key={t.topic.slug}
                          feature={feature}
                          topic={t.topic}
                          countOverride={t.count}
                          mini={true}
                          hidden={i > topicsToShow - 1}
                        />
                      ))}
                  </ul>

                  {topicsToShow < Object.keys(topicCounts).length ? (
                    <div className="p-4 mx-auto text-center">
                      <div
                        className="mx-auto text-gray-600 font-semibold text-xs opacity-75 hover:opacity-100 cursor-pointer"
                        onClick={() =>
                          setTopicsToShow(Object.keys(topicCounts).length)
                        }
                      >
                        Show All {Object.keys(topicCounts).length} →
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>
            ) : null}

            {faq.length ? <TopicPageFAQ faq={faq} /> : null}
          </div>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {count > 0 ? (
            <TopicStatsCard
              topic={null}
              featureDirectoryContext={{
                featureSlug: feature.slug,
                featureName: feature.name || unslugify(feature.slug),
              }}
              platformFilters={platformFilters}
              setPlatformFilters={setPlatformFilters}
              communities={communities}
              features={data.allFeatures.nodes}
              platforms={data.allPlatforms.nodes}
              sizeFilters={sizeFilters}
              setSizeFilters={setSizeFilters}
            />
          ) : null}
        </div>
      </div>
    </Layout>
  )
}

export const featurePageQuery = graphql`
  query FeaturePage($slug: String) {
    site {
      siteMetadata {
        title
      }
    }
    feature: googleSpreadsheetSourceFeatures(slug: { eq: $slug }) {
      slug
      name
      order
      sentenceEnding
      count
      icon
      iconActive
      description
      prefix
    }
    communities: allGoogleSpreadsheetSourceCommunities(
      filter: { removed: { ne: true }, suppress: { ne: true } }
      sort: { fields: members, order: DESC }
    ) {
      totalCount
      nodes {
        name
        slug
        members
        memberSize
        countFeatures
        order
        platform
        description
        topics
        logo
        forum
        chat
        apply
        pairing
        events
        perks
        jobs
        newsletter
        paid
        courses
        tools
        token
        reviewCount
        reviewAvg
        reviewsPositive
        gold
        sponsored
        visitors
        views
        redirects
        viewsPrev
        redirectsPrev
        claimed
        added
        updated
      }
    }
    topics: allGoogleSpreadsheetSourceTopics(filter: { count: { gt: 0 } }) {
      nodes {
        slug
        name
        count
        image
        people
        views
        viewsWithCommunities
        viewsWithCommunitiesPrev
      }
    }
    allPlatforms: allGoogleSpreadsheetSourcePlatforms(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        count
        logoBig
        logoSmall
        groupName
        groupNamePlural
        promoted
        removed
        descriptionForCommunities
      }
    }
    allFeatures: allGoogleSpreadsheetSourceFeatures(
      sort: { fields: name, order: ASC }
    ) {
      nodes {
        slug
        name
        order
        sentenceEnding
        count
        icon
        iconActive
        prefix
      }
    }
  }
`

export default FeaturePage
